/**
 * AIPanel.jsx — AI assistant sidebar panel
 *
 * Features:
 *   1. Natural language drawing ("draw a 3BHK flat 1200 sqft")
 *   2. Smart constraint inference ("suggest constraints for this drawing")
 *   3. Drawing review ("check my plan for errors")
 *   4. AI chat for CAD help ("how do I fillet a corner?")
 *
 * The panel sits as a right sidebar when toggled.
 * AI draw requests go through the ai-draw Edge Function (API key server-side).
 */
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { buildAIPrompt, callAIDrawEndpoint, applyAIMutations } from "../../engine/aiDrawing";
import { healGraph } from "../../engine/heal";

// Guard: supabase is null in local-only mode (env vars not set)
async function getSession() {
  if (!supabase) return null;
  const session = await getSession();
  return session;
}

const MONO = { fontFamily: "'JetBrains Mono','Fira Code',monospace" };

const QUICK_PROMPTS = [
  { label: "2BHK flat",        prompt: "Draw a 2BHK apartment floor plan 800 sqft with living room, 2 bedrooms, kitchen, 1 bathroom. Include all doors." },
  { label: "3BHK flat",        prompt: "Draw a 3BHK apartment floor plan 1200 sqft with living, dining, 3 bedrooms, 2 bathrooms, kitchen, balcony." },
  { label: "Ball bearing",     prompt: "Draw a ball bearing cross-section: OD=50mm ID=20mm shaft, 8 balls of 6mm diameter, inner race thickness 4mm, outer race thickness 4mm." },
  { label: "TV unit",          prompt: "Draw a TV unit elevation: 1800mm wide, 600mm tall, 3 bays, middle bay open, side bays with doors, 18mm MDF." },
  { label: "Kitchen layout",   prompt: "Draw an L-shaped kitchen layout 3500mm × 2800mm with cabinets along two walls, island in center 1200×900mm." },
  { label: "Site boundary",    prompt: "Draw a rectangular site boundary 20m × 15m with setbacks: 3m front, 1.5m sides, 3m rear." },
];

const REVIEW_PROMPT = (nodeCount, edgeCount, faceCount) =>
  `Review this architectural floor plan drawing: ${nodeCount} nodes, ${edgeCount} walls, ${faceCount} rooms detected. 
Check for: rooms with no door, walls not fully connected, structural issues, minimum room sizes (bedroom≥9sqm, living≥15sqm), missing bathroom, unreachable rooms. 
Respond in JSON: {"issues":[{"severity":"high|medium|low","message":"...","suggestion":"..."}], "summary":"..."}`;

const CONSTRAINT_PROMPT = (edgeCount) =>
  `This floor plan has ${edgeCount} walls. Suggest useful parametric constraints to improve the drawing: perpendicular walls, horizontal/vertical alignment, locked dimensions for standard sizes. 
Respond in JSON: {"suggestions":[{"type":"PERP|PARALLEL|LOCK_LENGTH|HORIZONTAL|VERTICAL","edges":"description","reason":"..."}], "count":N}`;

export default function AIPanel({ st, commit, onClose, user }) {
  const [mode,    setMode]    = useState("draw");   // draw | review | constraints | chat
  const [input,   setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const [msgs,    setMsgs]    = useState([
    { role: "assistant", text: "I can draw from natural language, review your plan, or suggest constraints. What would you like?" }
  ]);
  const [lastResult, setLastResult] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const addMsg = (role, text, extra) =>
    setMsgs(m => [...m, { role, text, ...extra }]);

  async function handleDraw(promptText) {
    if (!user) { addMsg("assistant", "Sign in to use AI drawing."); return; }
    setLoading(true);
    addMsg("user", promptText);
    try {
      const session = await getSession();
      const fullPrompt  = buildAIPrompt(promptText, st);
      const mutations   = await callAIDrawEndpoint(fullPrompt, session);
      const newSt       = healGraph(applyAIMutations(mutations, st));
      const added = {
        nodes:    Object.keys(newSt.nodes).length    - Object.keys(st.nodes).length,
        edges:    Object.keys(newSt.edges).length    - Object.keys(st.edges).length,
        circles:  Object.keys(newSt.circles||{}).length  - Object.keys(st.circles||{}).length,
        arcs:     Object.keys(newSt.arcs||{}).length     - Object.keys(st.arcs||{}).length,
      };
      setLastResult({ newSt, added, mutations });
      addMsg("assistant",
        `Generated ${mutations.length} operations: +${added.edges} walls, +${added.circles} circles, +${added.arcs} arcs. Preview applied — accept or discard.`,
        { type: "preview", newSt }
      );
    } catch (e) {
      addMsg("assistant", `Error: ${e.message}`);
    } finally { setLoading(false); }
  }

  async function handleReview() {
    if (!user) { addMsg("assistant", "Sign in to use AI review."); return; }
    setLoading(true);
    addMsg("user", "Review my floor plan for issues");
    try {
      const session = await getSession();
      const nodeCount = Object.keys(st.nodes).length;
      const edgeCount = Object.keys(st.edges).length;
      const faceCount = Object.keys(st.faces).length;
      if (edgeCount < 3) { addMsg("assistant", "Draw at least a few walls first, then I can review the plan."); setLoading(false); return; }
      const prompt   = REVIEW_PROMPT(nodeCount, edgeCount, faceCount);
      const mutations = await callAIDrawEndpoint(prompt, session);
      // mutations here is actually the review JSON — handle both cases
      addMsg("assistant", typeof mutations === "string" ? mutations : "Review complete — see issues below.", { type: "review", data: mutations });
    } catch (e) {
      addMsg("assistant", `Review failed: ${e.message}`);
    } finally { setLoading(false); }
  }

  async function handleConstraints() {
    if (!user) { addMsg("assistant", "Sign in to use AI constraints."); return; }
    setLoading(true);
    addMsg("user", "Suggest constraints for my drawing");
    try {
      const session = await getSession();
      const edgeCount = Object.keys(st.edges).length;
      if (edgeCount < 2) { addMsg("assistant", "Draw at least 2 walls first."); setLoading(false); return; }
      const prompt   = CONSTRAINT_PROMPT(edgeCount);
      const result   = await callAIDrawEndpoint(prompt, session);
      addMsg("assistant", `Found ${Array.isArray(result) ? result.length : "several"} constraint suggestions.`, { type: "constraints", data: result });
    } catch (e) {
      addMsg("assistant", `Failed: ${e.message}`);
    } finally { setLoading(false); }
  }

  function acceptLastResult() {
    if (!lastResult) return;
    commit(() => lastResult.newSt);
    addMsg("assistant", "Applied to canvas. ✓");
    setLastResult(null);
  }

  function discardLastResult() {
    setLastResult(null);
    addMsg("assistant", "Discarded. Try a different description.");
  }

  async function handleSend() {
    const text = input.trim(); if (!text) return;
    setInput("");
    if (mode === "draw")        await handleDraw(text);
    else if (mode === "review") await handleReview();
    else                        await handleDraw(text); // default: draw
  }

  const S = {
    panel: { width: 280, height: "100%", background: "#03070d", borderLeft: "0.5px solid #0d1520",
             display: "flex", flexDirection: "column", flexShrink: 0, ...MONO },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 12px", borderBottom: "0.5px solid #0d1520" },
    tabs:   { display: "flex", gap: 3, padding: "6px 10px", borderBottom: "0.5px solid #0d1520" },
    tab:    (active, color="#3b82f6") => ({
      padding: "3px 9px", borderRadius: 3, fontSize: 9, cursor: "pointer", border: "none", ...MONO,
      background: active ? color+"25" : "transparent",
      color: active ? color : "#2a3a4a",
    }),
    msgs: { flex: 1, overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 },
    bubble: (role) => ({
      maxWidth: "90%", padding: "7px 10px", borderRadius: 8, fontSize: 10, lineHeight: 1.7,
      alignSelf: role === "user" ? "flex-end" : "flex-start",
      background: role === "user" ? "#0c1828" : "#06111e",
      border: `0.5px solid ${role === "user" ? "#1e3d66" : "#0d1520"}`,
      color: role === "user" ? "#60a5fa" : "#8496a8",
    }),
    input: { padding: "8px 10px", borderTop: "0.5px solid #0d1520", display: "flex", gap: 6, alignItems: "flex-end" },
    textarea: { flex: 1, background: "#06111e", border: "0.5px solid #1e2a36", borderRadius: 5,
                color: "#dde6f0", fontSize: 10, padding: "6px 8px", resize: "none", outline: "none",
                fontFamily: "inherit", minHeight: 40, maxHeight: 120 },
    send: { padding: "6px 10px", background: "#0c1828", border: "0.5px solid #3b82f640",
            color: "#3b82f6", borderRadius: 5, cursor: "pointer", fontSize: 9, ...MONO },
  };

  return (
    <div style={S.panel}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 9, color: "#3b82f6", fontWeight: 700, letterSpacing: 1.5 }}>AI ASSISTANT</span>
          {!user && <span style={{ fontSize: 8, color: "#EF9F27", letterSpacing: 0.5 }}>sign in required</span>}
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#2a3a4a", cursor: "pointer", fontSize: 13 }}>✕</button>
      </div>

      {/* Mode tabs */}
      <div style={S.tabs}>
        {[["draw","Draw","#3b82f6"],["review","Review","#4ade80"],["constraints","Constraints","#818cf8"]].map(([id,label,col]) => (
          <button key={id} style={S.tab(mode===id, col)} onClick={() => setMode(id)}>{label}</button>
        ))}
      </div>

      {/* Quick prompts (draw mode only) */}
      {mode === "draw" && (
        <div style={{ padding: "6px 10px", borderBottom: "0.5px solid #0d1520" }}>
          <div style={{ fontSize: 8, color: "#1e2a36", letterSpacing: 1, marginBottom: 5 }}>QUICK DRAW</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {QUICK_PROMPTS.map(qp => (
              <button key={qp.label} onClick={() => handleDraw(qp.prompt)} disabled={loading}
                style={{ padding: "2px 7px", background: "#06111e", border: "0.5px solid #0d1520",
                  color: "#3d5068", borderRadius: 3, cursor: "pointer", fontSize: 8, ...MONO }}>
                {qp.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message thread */}
      <div style={S.msgs}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column",
            alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={S.bubble(m.role)}>{m.text}</div>
            {/* Accept/Discard buttons for preview */}
            {m.type === "preview" && m === msgs[msgs.length - 1] && lastResult && (
              <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
                <button onClick={acceptLastResult}
                  style={{ padding: "3px 10px", background: "#0c2318", border: "0.5px solid #4ade8040",
                    color: "#4ade80", borderRadius: 3, cursor: "pointer", fontSize: 9, ...MONO }}>
                  ✓ Accept
                </button>
                <button onClick={discardLastResult}
                  style={{ padding: "3px 10px", background: "#140808", border: "0.5px solid #f8717140",
                    color: "#f87171", borderRadius: 3, cursor: "pointer", fontSize: 9, ...MONO }}>
                  ✕ Discard
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: "flex-start", padding: "7px 10px", background: "#06111e",
            border: "0.5px solid #0d1520", borderRadius: 8, fontSize: 10, color: "#1e2a36" }}>
            Thinking…
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {/* Input area */}
      <div style={S.input}>
        {mode === "review" || mode === "constraints" ? (
          <button onClick={mode === "review" ? handleReview : handleConstraints}
            disabled={loading}
            style={{ ...S.send, flex: 1, color: mode === "review" ? "#4ade80" : "#818cf8",
              border: `0.5px solid ${mode === "review" ? "#4ade8040" : "#818cf840"}`,
              background: mode === "review" ? "#0c2318" : "#12102a" }}>
            {loading ? "Working…" : mode === "review" ? "Review my plan" : "Suggest constraints"}
          </button>
        ) : (
          <>
            <textarea
              style={S.textarea}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={"Describe what to draw…\ne.g. 3BHK flat 1200 sqft"}
              rows={2}
            />
            <button onClick={handleSend} disabled={loading || !input.trim()} style={S.send}>
              {loading ? "…" : "→"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
